import React from 'react';
import cn from 'classnames';

import { getImageContainerStyle } from 'widget/utils/getImageStyle';
import ClapperboardIcon from 'widget/shared/icons/ClapperboardIcon';
import PlayIcon from 'widget/shared/icons/PlayIcon';
import { IconColor, IconSize } from 'widget/shared/icons/BaseIcon';

import MessageMediaFallback from '../MessageMediaFallback';

import { useVideoPoster } from './useVideoPoster';
import styles from './MessageVideo.module.scss';

export type MessageVideoProps = {
  url: string;
  filename: string;
  size: number;
  width?: number;
  height?: number;
  previewUrl?: string;
  isFromBot?: boolean;
  className?: string;
  onClick?: () => void;
};

const MessageVideo: React.FC<MessageVideoProps> = ({
  url,
  filename,
  size,
  width,
  height,
  previewUrl,
  isFromBot = false,
  onClick,
  className
}) => {
  const [hasError, setHasError] = React.useState(false);
  const { poster, preload, showPlaceholder, handleLoadedData } =
    useVideoPoster(previewUrl);

  const handleClick = React.useCallback(() => {
    if (onClick) {
      onClick();
    } else {
      window.open(url, '_blank');
    }
  }, [onClick, url]);

  const handleError = React.useCallback(() => {
    setHasError(true);
  }, []);

  if (hasError) {
    return (
      <MessageMediaFallback
        url={url}
        filename={filename}
        size={size}
        variant={isFromBot ? 'default' : 'user'}
      />
    );
  }

  const containerStyle = getImageContainerStyle({ width, height });

  return (
    <div
      className={cn(
        styles['message-video'],
        isFromBot
          ? styles['message-video_from-bot']
          : styles['message-video_from-user'],
        className
      )}
      style={containerStyle}
      onClick={handleClick}
    >
      {showPlaceholder && (
        <div className={styles['message-video__placeholder']}>
          <ClapperboardIcon
            className={styles['message-video__placeholder-icon']}
            color={IconColor.White}
          />
        </div>
      )}
      <video
        src={url}
        poster={poster}
        preload={preload}
        muted
        playsInline
        style={{ pointerEvents: 'none' }}
        onError={handleError}
        onLoadedData={handleLoadedData}
        className={styles['message-video__video']}
      />
      <PlayIcon
        className={styles['message-video__play-icon']}
        color={IconColor.Main}
        size={IconSize.XXL}
      />
    </div>
  );
};

export default React.memo(MessageVideo);
